import React, { ReactNode, useMemo } from "react";
import {
	Button,
	cn,
	DataObserveKey,
	Icon,
	IconClose,
	InlineText,
	Ul,
	Tooltip,
	useUniqueId,
	VisualComponent,
} from "@siteimprove/fancylib";
import { useLabTranslations } from "../../../translations/translations";
import { KeyCode } from "../../../utils/keyboard-nav-utils";
import { dataObserveKeyDiscriminator } from "../../../utils/shorthands";
import {
	CompareFunction,
	indexOfOption,
	isOptionItem,
	OptionItem,
	SelectItem,
} from "./select-listbox";
import * as scss from "./select-overview-list.scss";
import * as listboxScss from "./select-listbox.scss";

export type SelectOverviewListProps<T> = {
	/** Custom unique id */
	id: string;
	/** List of options or groups of options */
	items: SelectItem<T>[];
	/** Selected options */
	value: OptionItem<T>[];
	/** Callback for onChange event */
	onDeselect: (value: OptionItem<T>) => void;
	/** Label that describes the overview list */
	label: string;
	/** Disable the clear button on each list items */
	disabled?: boolean;
	/** Function that returns true if two items are equal */
	compareFn?: CompareFunction<T>;
	/** Should the overview list be full width? */
	fullWidth?: boolean;
	/** Custom renderer for options */
	optionRenderer?: (props: OverviewOptionProps<T>) => JSX.Element;
} & VisualComponent &
	DataObserveKey;

export function SelectOverviewList<T>(props: SelectOverviewListProps<T>): JSX.Element | null {
	const {
		className,
		disabled,
		fullWidth,
		id,
		items,
		label,
		onDeselect,
		optionRenderer,
		style,
		value: selectedOptions,
		compareFn,
		...rest
	} = props;

	const selectedItems = useMemo<SelectItem<T>[]>(
		() => filterSelectedItems(items),
		[items, selectedOptions]
	);

	const labelId = useUniqueId("select-overview-label");

	return selectedItems.length === 0 ? null : (
		<div
			id={id}
			className={cn(
				scss.overviewList,
				className,
				fullWidth && scss.fullWidth,
				"fancy-SelectOverview"
			)}
			style={style}
			{...rest}
		>
			<InlineText
				className={cn(scss.label)}
				emphasis="medium"
				id={labelId}
				size="xSmall"
				tone="neutralDark"
			>
				{label}
			</InlineText>
			<Ul
				className={cn(scss.overviewListbox)}
				aria-labelledby={labelId}
				items={selectedItems.map((item) =>
					isOptionItem(item)
						? renderOption(item)
						: item.items.length > 0 && (
								<GroupContainer key={item.heading} heading={item.heading}>
									{item.items.map(renderOption)}
								</GroupContainer>
						  )
				)}
			/>
		</div>
	);

	function filterSelectedItems(items: SelectItem<T>[]): SelectItem<T>[] {
		const selectedItems: SelectItem<T>[] = [];
		items.forEach((item: SelectItem<T>) => {
			if (isOptionItem(item) && isSelected(item)) return selectedItems.push(item);
			if (!isOptionItem(item)) {
				const selectedGroupItems = item.items.filter(isSelected);
				if (selectedGroupItems.length > 0) {
					selectedItems.push({
						...item,
						items: selectedGroupItems,
					});
				}
			}
		});
		return selectedItems;
	}

	function isSelected(option: OptionItem<T>): boolean {
		return indexOfOption(selectedOptions, option, compareFn) > -1;
	}

	function renderOption(option: OptionItem<T>): JSX.Element {
		const baseProps = {
			item: option,
			onDeselect,
			disabled,
			"data-observe-key": dataObserveKeyDiscriminator(
				option["data-observe-key"],
				"SelectOverview",
				"ClearButton"
			),
		};

		if (optionRenderer) {
			return optionRenderer(baseProps);
		}

		return <OverviewOption {...baseProps} key={option.title} />;
	}
}

type GroupContainerProps = {
	heading: string;
	children: ReactNode[];
};

const GroupContainer = (props: GroupContainerProps): JSX.Element => {
	const { children, heading } = props;
	const headingId = useUniqueId("overview-list-group-container");
	return (
		<div role="group" aria-labelledby={headingId}>
			<InlineText
				tone="subtle"
				size="large"
				aria-hidden="true"
				className={cn(listboxScss.groupContainerHeading)}
				id={headingId}
			>
				{heading}
			</InlineText>
			<Ul items={children} />
		</div>
	);
};

export type OverviewOptionProps<T> = {
	item: OptionItem<T>;
	onDeselect: (value: OptionItem<T>) => void;
	disabled?: boolean;
} & DataObserveKey;

function OverviewOption<T>(props: OverviewOptionProps<T>): JSX.Element {
	const {
		item: { title, description, icon },
	} = props;

	return (
		<BaseOverviewOption {...props}>
			<div className={cn(listboxScss.optionTitleContainer)} aria-hidden="true">
				{icon}
				<InlineText tone="neutralDark" lineHeight="multi-line" emphasis="normal">
					{title}
				</InlineText>
			</div>
			{description && (
				<InlineText size="xSmall" tone="subtle" lineHeight="multi-line">
					{description}
				</InlineText>
			)}
		</BaseOverviewOption>
	);
}

export type BaseOverviewOptionProps<T> = React.PropsWithChildren<OverviewOptionProps<T>>;

export function BaseOverviewOption<T>(props: BaseOverviewOptionProps<T>): JSX.Element {
	const { item, onDeselect, disabled, children } = props;
	const { title } = item;
	const i18n = useLabTranslations();
	const tooltipContent = `${i18n.deselect}: ${title}`;

	return (
		<div className={cn(scss.overviewOption)} tabIndex={1}>
			<div className={cn(scss.optionMainContainer)}>{children}</div>
			<Tooltip variant={{ type: "interactive" }} content={tooltipContent}>
				<Button
					disabled={disabled}
					variant="borderless"
					onClick={() => onDeselect(item)}
					onKeyDown={(e) => e.keyCode === KeyCode.Delete && onDeselect(item)}
					data-observe-key={props["data-observe-key"]}
				>
					<Icon>
						<IconClose />
					</Icon>
				</Button>
			</Tooltip>
		</div>
	);
}
